/**
 * Aamra Custom Search Functionality
 * Handles recent searches, popular searches, real-time typing interactions,
 * fuzzy matching zero-state logic, and search correction event logging.
 */
(function() {
  'use strict';

  // Helper: HTML escaping
  function aamraEscapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Helper: Highlight query matches in predictive results
  function aamraHighlightQueryMatches(container, query) {
    if (!container || !query || typeof query !== 'string' || !query.trim()) return;
    var rawQuery = query.trim();
    
    var termsSet = [rawQuery];
    var words = rawQuery.split(/\s+/).filter(function(w) { return w.length > 0; });
    words.forEach(function(w) {
      if (termsSet.indexOf(w) === -1) termsSet.push(w);
    });

    var typos = {
      'ncecklcae': 'necklace',
      'necklcae': 'necklace',
      'neclace': 'necklace',
      'erring': 'earring',
      'errings': 'earrings',
      'earings': 'earrings',
      'braclet': 'bracelet',
      'braclets': 'bracelets',
      'diamnd': 'diamond',
      'dimond': 'diamond'
    };
    var lowerQ = rawQuery.toLowerCase();
    if (typos[lowerQ] && termsSet.indexOf(typos[lowerQ]) === -1) {
      termsSet.push(typos[lowerQ]);
    }

    termsSet.sort(function(a, b) { return b.length - a.length; });

    var escapedTerms = termsSet.map(function(t) {
      return t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    });
    var pattern = '(' + escapedTerms.join('|') + ')';

    var items = container.querySelectorAll('#predictive-search-results-queries-list a, .predictive-search-item');
    items.forEach(function(el) {
      var rawText = el.getAttribute('data-aamra-query') || el.textContent.trim();
      if (!rawText) return;
      el.setAttribute('data-aamra-query', rawText);

      try {
        var elRegex = new RegExp(pattern, 'gi');
        el.innerHTML = rawText.replace(elRegex, function(m) {
          return '<strong class="aamra-match-bold font-bold">' + aamraEscapeHtml(m) + '</strong>';
        });
      } catch (err) {
        el.textContent = rawText;
      }
    });
  }

  // Helper: Synchronize search inputs to 'Search' placeholder
  function aamraSyncSearchPlaceholder() {
    var ph = 'Search';
    var inputs = document.querySelectorAll('#header-search-in-modal, #header-search-in-modal-mobile, #search-in-modal-mobile-dock, .input-search');
    inputs.forEach(function(el) {
      el.placeholder = ph;
    });
  }

  // Helper: Initialize Alpine directives & components inside injected search results
  function aamraInitResultCards() {
    if (window.Alpine && window.Alpine.initTree) {
      setTimeout(function() {
        var resultContainer = document.getElementById('search-result');
        if (resultContainer) {
          window.Alpine.initTree(resultContainer);
        }
      }, 40);
    }
  }

  // Alpine Component: Search Modal Data
  function aamraSearchModalData(config) {
    config = config || {};
    return {
      open_search: false,
      query: '',
      result: '',
      loading: false,
      showSuggest: true,
      cachedResults: {},
      t: null,
      abortController: null,
      searchType: config.searchType || 'query,product,page',
      limitDesktop: 10,
      limitMobile: 10,
      rootUrl: (config.rootUrl || ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/')).replace(/\/$/, '') + '/',
      searchPlaceholder: 'Search',
      updatePlaceholder: function() {
        this.searchPlaceholder = 'Search';
      },
      init: function() {
        this.updatePlaceholder();
      },
      openModal: function() {
        var self = this;
        this.open_search = true;
        this.updatePlaceholder();
        if (!this.query || this.query === 'undefined') {
          this.query = '';
        }
        if (this.query.trim() === '') {
          this.showSuggest = true;
        }
        setTimeout(function() {
          var input = document.getElementById('header-search-in-modal');
          if (input) {
            if (input.value === 'undefined' || !input.value) {
              input.value = '';
            }
            input.placeholder = self.searchPlaceholder;
            input.focus();
          }
        }, 50);
        window.aamraSearch && window.aamraSearch.renderRecent();
        window.aamraSearch && window.aamraSearch.loadPopularFromApp && window.aamraSearch.loadPopularFromApp();
      },
      closeModal: function() {
        this.open_search = false;
        this.query = '';
        this.result = '';
        this.showSuggest = true;
        var input = document.getElementById('header-search-in-modal');
        if (input) {
          input.value = '';
          try { input.blur(); } catch(e) {}
        }
        try {
          var viewportMeta = document.querySelector('meta[name="viewport"]');
          if (viewportMeta) {
            var prevContent = viewportMeta.getAttribute('content') || 'width=device-width, initial-scale=1';
            viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0');
            if (document.activeElement && typeof document.activeElement.blur === 'function') {
              document.activeElement.blur();
            }
            setTimeout(function() {
              viewportMeta.setAttribute('content', prevContent);
            }, 300);
          }
        } catch(e) {}
      },
      onInput: function(val) {
        var self = this;
        val = (val && typeof val === 'string' && val !== 'undefined') ? val : '';
        this.query = val;
        clearTimeout(this.t);
        if (val.trim() === '') {
          if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
          }
          this.showSuggest = true;
          this.result = '';
          this.loading = false;
          window.aamraSearch && window.aamraSearch.renderRecent();
        } else {
          this.showSuggest = false;
          var term = val.trim();
          var limit = 10;
          var cacheKey = term.toLowerCase() + '_' + limit;
          if (this.cachedResults[cacheKey]) {
            this.result = this.cachedResults[cacheKey];
            this.loading = false;
            aamraInitResultCards();
            return;
          }
          this.result = '';
          this.loading = true;
          this.t = setTimeout(function() {
            self.fetchPredictive(term);
          }, 150);
        }
      },
      fetchPredictive: function(term) {
        var self = this;
        if (!term || term === 'undefined') return;
        var limit = 10;
        var cacheKey = term.toLowerCase() + '_' + limit;
        if (this.cachedResults[cacheKey]) {
          this.result = this.cachedResults[cacheKey];
          this.loading = false;
          aamraInitResultCards();
          return;
        }

        var cleanTerm = (term || '').toLowerCase().trim();
        // Immediately render zero-state for 'default' or variant 'default title'
        if (cleanTerm === 'default' || cleanTerm === 'default title' || cleanTerm.indexOf('default') !== -1) {
          this.result = '<div class="text-center py-10 px-4 text-base font-sans text-[rgb(var(--colors-heading))]"><p class="text-base font-semibold mb-1">No results for &lsquo;' + aamraEscapeHtml(term) + '&rsquo;</p><p class="text-xs text-[rgba(var(--colors-text),0.7)]">Check your spelling or try searching for another term</p></div>';
          this.cachedResults[cacheKey] = this.result;
          this.loading = false;
          return;
        }

        if (this.abortController) {
          this.abortController.abort();
        }
        this.abortController = new AbortController();

        this.loading = true;
        var root = (this.rootUrl || ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/')).replace(/\/$/, '') + '/';
        var url = root + 'search/suggest?q=' + encodeURIComponent(term) + '&resources[type]=query,product&resources[limit]=10&resources[limit_scope]=each&resources[options][fields]=title,body&resources[options][prefix]=none&section_id=predictive-search';
        
        var fetchSection = function(reqUrl) {
          return fetch(reqUrl, { signal: self.abortController.signal })
            .then(function(res) {
              if (!res.ok) throw new Error('Search HTTP ' + res.status);
              return res.text();
            })
            .then(function(html) {
              var parser = new DOMParser();
              var doc = parser.parseFromString(html, 'text/html');
              var sectionEl = doc.getElementById('shopify-section-predictive-search') || doc.querySelector('[id*="predictive-search"]') || doc.body;
              if (sectionEl && sectionEl.innerHTML.trim() !== '') {
                // Dynamic search term stemming (singular & plural)
                var termClean = cleanTerm.replace(/[-_]/g, ' ');
                var termSingular = termClean;
                var termPlural = termClean;
                var isMultiWord = termClean.indexOf(' ') !== -1;
                var searchWords = isMultiWord ? termClean.split(/\s+/).filter(Boolean) : [];

                if (!isMultiWord && termClean.length > 2) {
                  if (termClean.slice(-1) === 's') {
                    termSingular = termClean.slice(0, -1);
                  } else {
                    termPlural = termClean + 's';
                  }
                }

                var prefixTerm = ' ' + termSingular;
                var prefixPlural = ' ' + termPlural;
                var prefixRaw = ' ' + termClean;

                var dynamicCheckMatch = function(rawText) {
                  if (!rawText) return false;
                  var tClean = rawText.toLowerCase().replace(/[-_/,\.()]/g, ' ');
                  var tPadded = ' ' + tClean + ' ';

                  if (isMultiWord) {
                    if (tClean.indexOf(termClean) !== -1) return true;
                    return searchWords.every(function(w) {
                      return tPadded.indexOf(' ' + w) !== -1;
                    });
                  }
                  return tPadded.indexOf(prefixTerm) !== -1 ||
                         tPadded.indexOf(prefixPlural) !== -1 ||
                         tPadded.indexOf(prefixRaw) !== -1;
                };

                var productGrid = sectionEl.querySelector('.predictive-search-others ul[role="listbox"]');
                if (productGrid) {
                  var cards = Array.from(productGrid.querySelectorAll('li[role="option"]'));
                  if (cards.length > 1) {
                    var checkPriority = function(item) {
                      var titleEl = item.querySelector('.x-card-title') || item;
                      var itemText = titleEl.textContent || '';
                      if (dynamicCheckMatch(itemText)) return 0; // Tier 1: Direct title match
                      return 1; // Tier 2: Secondary match
                    };

                    cards.sort(function(a, b) {
                      return checkPriority(a) - checkPriority(b);
                    });

                    cards.forEach(function(card, idx) {
                      card.id = 'predictive-search-option-' + (idx + 1);
                      if (idx >= 4) {
                        card.classList.add('flex', 'lg:hidden');
                        card.classList.remove('md:hidden');
                      } else {
                        card.classList.remove('lg:hidden', 'md:hidden');
                      }
                      productGrid.appendChild(card);
                    });
                  }
                }

                var queryList = sectionEl.querySelector('#predictive-search-results-queries-list');
                if (queryList) {
                  var qItems = Array.from(queryList.querySelectorAll('li'));
                  if (qItems.length > 1) {
                    var checkQPriority = function(qItem) {
                      var qText = qItem.textContent || '';
                      if (dynamicCheckMatch(qText)) return 0;
                      return 1;
                    };
                    qItems.sort(function(a, b) {
                      return checkQPriority(a) - checkQPriority(b);
                    });
                    qItems.forEach(function(qItem) {
                      queryList.appendChild(qItem);
                    });
                  }
                }

                  var remProducts = sectionEl.querySelectorAll('.predictive-search-others li[role="option"]');
                  var remQueries = sectionEl.querySelectorAll('#predictive-search-results-queries-list li');
                  if (remProducts.length === 0) {
                    var othersWrap = sectionEl.querySelector('.predictive-search-others');
                    if (othersWrap) othersWrap.remove();
                  }
                  if (remQueries.length === 0) {
                    var queriesWrap = sectionEl.querySelector('.search-result-query-and-page');
                    if (queriesWrap) queriesWrap.remove();
                  }

                  var hasAny = sectionEl.querySelector('.predictive-search-others li, #predictive-search-results-queries-list li, .predictive-search-pages-wrap li');
                  if (!hasAny) {
                    sectionEl.innerHTML = '<div class="text-center py-10 px-4 text-base font-sans text-[rgb(var(--colors-heading))]"><p class="text-base font-semibold mb-1">No results for &lsquo;' + aamraEscapeHtml(term) + '&rsquo;</p><p class="text-xs text-[rgba(var(--colors-text),0.7)]">Check your spelling or try searching for another term</p></div>';
                  }

                aamraHighlightQueryMatches(sectionEl, term);
                self.result = sectionEl.innerHTML;
                self.cachedResults[cacheKey] = self.result;
                aamraInitResultCards();
              } else {
                self.result = '<div class="text-center py-10 px-4 text-base font-sans text-[rgb(var(--colors-heading))]"><p class="text-base font-semibold mb-1">No results for &lsquo;' + aamraEscapeHtml(term) + '&rsquo;</p><p class="text-xs text-[rgba(var(--colors-text),0.7)]">Check your spelling or try searching for another term</p></div>';
                self.cachedResults[cacheKey] = self.result;
              }
            });
        };

        fetchSection(url)
          .catch(function(err) {
            if (err.name === 'AbortError') return;
            var fallbackUrl = root + 'search/suggest?q=' + encodeURIComponent(term) + '&resources[type]=product&resources[limit]=10&resources[options][fields]=title,body&resources[options][prefix]=none&section_id=predictive-search';
            return fetchSection(fallbackUrl);
          })
          .catch(function(err) {
            if (err.name === 'AbortError') return;
            console.error('Predictive search error:', err);
            self.result = '<div class="text-center py-10 px-4 text-base font-sans text-[rgb(var(--colors-heading))]"><p class="text-base font-semibold mb-1">No results for &lsquo;' + aamraEscapeHtml(term) + '&rsquo;</p><p class="text-xs text-[rgba(var(--colors-text),0.7)]">Check your spelling or try searching for another term</p></div>';
          })
          .finally(function() {
            self.loading = false;
          });
      }
    };
  }

  // 1. Initialize Alpine Search Modal Store & Components
  function initSearchModalComponents() {
    if (window.Alpine) {
      if (!window.Alpine.store('xSearchModal')) {
        window.Alpine.store('xSearchModal', {
          open: false,
          openModal: function() {
            window.dispatchEvent(new CustomEvent('open-search-modal'));
          },
          closeModal: function() {
            window.dispatchEvent(new CustomEvent('close-search-modal'));
          }
        });
      }
      window.Alpine.data('aamraSearchModalData', aamraSearchModalData);
    }
  }

  if (window.Alpine) {
    initSearchModalComponents();
  } else {
    document.addEventListener('alpine:init', initSearchModalComponents);
  }

  // Placeholder Sync Listeners
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', aamraSyncSearchPlaceholder);
  } else {
    aamraSyncSearchPlaceholder();
  }
  window.addEventListener('resize', aamraSyncSearchPlaceholder);

  // Expose to window for global access and non-Alpine invocations
  window.aamraEscapeHtml = aamraEscapeHtml;
  window.aamraHighlightQueryMatches = aamraHighlightQueryMatches;
  window.aamraSyncSearchPlaceholder = aamraSyncSearchPlaceholder;
  window.aamraSearchModalData = aamraSearchModalData;

  // 2. Aamra Search State & Utilities
  window.aamraSearch = {
    getRecent: function() {
      try {
        var s = localStorage.getItem('aamra_recent_searches');
        if (s) {
          var parsed = JSON.parse(s);
          if (Array.isArray(parsed) && parsed.length > 0) return parsed;
        }
      } catch (e) {}
      return [];
    },

    addRecent: function(term) {
      if (!term || typeof term !== 'string' || !term.trim()) return;
      term = term.trim();
      try {
        var list = this.getRecent().filter(function(i) {
          return i.toLowerCase() !== term.toLowerCase();
        });
        list.unshift(term);
        if (list.length > 5) list = list.slice(0, 5);
        localStorage.setItem('aamra_recent_searches', JSON.stringify(list));
        this.renderRecent();
      } catch (e) {}
    },

    removeRecent: function(term) {
      if (!term) return;
      try {
        var list = this.getRecent().filter(function(i) {
          return i.toLowerCase() !== term.toLowerCase();
        });
        localStorage.setItem('aamra_recent_searches', JSON.stringify(list));
        this.renderRecent();
      } catch (e) {}
    },

    clearRecent: function() {
      try {
        localStorage.removeItem('aamra_recent_searches');
        this.renderRecent();
      } catch (e) {}
    },

    renderRecent: function() {
      var terms = this.getRecent();
      var containers = document.querySelectorAll('.aamra-recent-searches-list');

      containers.forEach(function(c) {
        c.innerHTML = '';
        if (!terms || terms.length === 0) {
          var empty = document.createElement('div');
          empty.className = 'aamra-recent-empty';
          empty.textContent = 'No recent searches';
          c.appendChild(empty);
          return;
        }
        terms.forEach(function(term) {
          var row = document.createElement('div');
          row.className = 'aamra-recent-row';

          var item = document.createElement('button');
          item.type = 'button';
          item.className = 'aamra-recent-item';
          item.setAttribute('data-aamra-search-pill', term);
          item.textContent = term;

          var removeBtn = document.createElement('button');
          removeBtn.type = 'button';
          removeBtn.className = 'aamra-recent-remove';
          removeBtn.setAttribute('aria-label', 'Remove ' + term);
          removeBtn.innerHTML = '<svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 1L11 11M11 1L1 11"></path></svg>';
          removeBtn.addEventListener('click', function(e) {
            e.stopPropagation();
            window.aamraSearch.removeRecent(term);
          });

          row.appendChild(item);
          row.appendChild(removeBtn);
          c.appendChild(row);
        });
      });
    },

    loadPopularFromApp: function() {
      var wrap = document.getElementById('aamra-popular-pills');
      if (!wrap) return;

      // Check if third-party search app provides popular/trending searches
      if (window.Searchanise && window.Searchanise.PopularSearches) {
        try {
          var sQueries = window.Searchanise.PopularSearches;
          if (Array.isArray(sQueries) && sQueries.length > 0) {
            wrap.innerHTML = '';
            sQueries.slice(0, 3).forEach(function(item) {
              var term = typeof item === 'string' ? item : (item.title || item.query);
              if (term) {
                var btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'aamra-pill-btn';
                btn.setAttribute('data-aamra-search-pill', term);
                btn.textContent = term;
                wrap.appendChild(btn);
              }
            });
            return;
          }
        } catch (e) {}
      }

      // Fallback: Check Shopify Search & Discovery predictive search suggestions
      var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      rootUrl = rootUrl.replace(/\/$/, '') + '/';

      fetch(rootUrl + 'search/suggest.json?q=*&resources[type]=query&resources[limit]=3')
        .then(function(res) { return res.json(); })
        .then(function(data) {
          var queries = data && data.resources && data.resources.results && data.resources.results.queries;
          if (queries && queries.length > 0) {
            wrap.innerHTML = '';
            queries.slice(0, 3).forEach(function(q) {
              if (q && q.text) {
                var btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'aamra-pill-btn';
                btn.setAttribute('data-aamra-search-pill', q.text);
                btn.textContent = q.text;
                wrap.appendChild(btn);
              }
            });
          }
        })
        .catch(function() {});
    },

    applyPill: function(term) {
      if (!term || typeof term !== 'string' || term === 'undefined') return;
      term = term.trim();
      if (!term || term === 'undefined') return;
      this.addRecent(term);

      var input = document.getElementById('header-search-in-modal') || 
                  document.getElementById('search-in-modal') || 
                  document.querySelector('input[name="q"]');
      if (input) {
        input.value = term;

        var modal = input.closest('.search-modal-root') || input.closest('[x-data]');
        if (modal && modal._x_dataStack && modal._x_dataStack[0]) {
          var data = modal._x_dataStack[0];
          if (typeof data.onInput === 'function') {
            data.onInput(term);
          } else {
            data.showSuggest = false;
            if (typeof data.getSearchResult === 'function') {
              data.getSearchResult(term);
            }
          }
        }

        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.focus();
      }
    }
  };

  // 3. Zero-Results Fuzzy Match & Search Correction Logging
  // 3. Zero-Results Fuzzy Match & Search Correction Logging
  function damerauLevenshtein(s1, s2) {
    var len1 = s1.length, len2 = s2.length;
    var d = [];
    for (var i = 0; i <= len1; i++) {
      d[i] = [];
      d[i][0] = i;
    }
    for (var j = 0; j <= len2; j++) {
      d[0][j] = j;
    }
    for (var i = 1; i <= len1; i++) {
      for (var j = 1; j <= len2; j++) {
        var cost = s1[i - 1] === s2[j - 1] ? 0 : 1;
        d[i][j] = Math.min(
          d[i - 1][j] + 1,       // deletion
          d[i][j - 1] + 1,       // insertion
          d[i - 1][j - 1] + cost // substitution
        );
        if (i > 1 && j > 1 && s1[i - 1] === s2[j - 2] && s1[i - 2] === s2[j - 1]) {
          d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1); // transposition
        }
      }
    }
    return d[len1][len2];
  }

  function findFuzzyMatch(query, dict) {
    if (!query || typeof query !== 'string') return null;
    var cleanQuery = query.toLowerCase().trim();
    if (cleanQuery.length < 3 || cleanQuery === 'default' || cleanQuery.indexOf('default') !== -1) return null;

    // If query is an exact match for a dictionary term, it is a correct spelling!
    if (Array.isArray(dict)) {
      for (var k = 0; k < dict.length; k++) {
        if (dict[k].toLowerCase().trim() === cleanQuery) {
          return null;
        }
      }
    }

    // Direct mappings for known common typos and misspellings
    var commonMisspellings = {
      'ncecklcae': 'necklaces',
      'necklcae': 'necklaces',
      'necklece': 'necklaces',
      'neclace': 'necklaces',
      'neclaces': 'necklaces',
      'erring': 'earrings',
      'errings': 'earrings',
      'earings': 'earrings',
      'earling': 'earrings',
      'rng': 'rings',
      'rngs': 'rings',
      'rigs': 'rings',
      'braclet': 'bracelets',
      'braclets': 'bracelets',
      'barcelet': 'bracelets',
      'pnedant': 'pendants',
      'pedant': 'pendants',
      'diamnd': 'diamond',
      'dimond': 'diamond',
      'daimond': 'diamond',
      'diomand': 'diamond',
      'diamon': 'diamond'
    };
    if (commonMisspellings[cleanQuery]) {
      return commonMisspellings[cleanQuery];
    }

    var bestMatch = null;
    var bestDistance = 999;
    var bestSimilarity = 0;

    for (var i = 0; i < dict.length; i++) {
      var candidate = dict[i].toLowerCase().trim();
      if (candidate === cleanQuery) continue;

      var dist = damerauLevenshtein(cleanQuery, candidate);
      var maxLen = Math.max(cleanQuery.length, candidate.length);
      var similarity = 1 - (dist / maxLen);

      var threshold = cleanQuery.length <= 4 ? 1 : (cleanQuery.length <= 7 ? 2 : 3);
      if (dist <= threshold && similarity > 0.60) {
        if (similarity > bestSimilarity || (similarity === bestSimilarity && dist < bestDistance)) {
          bestSimilarity = similarity;
          bestDistance = dist;
          bestMatch = dict[i];
        }
      }
    }

    // If query ends in 's', prefer plural category if in dict
    if (bestMatch && cleanQuery.endsWith('s') && !bestMatch.endsWith('s')) {
      var pluralCandidate = bestMatch + 's';
      for (var j = 0; j < dict.length; j++) {
        if (dict[j].toLowerCase().trim() === pluralCandidate) {
          bestMatch = dict[j];
          break;
        }
      }
    }

    return bestMatch;
  }

  function initFuzzySearch() {
    var searchPageWrap = document.querySelector('[data-aamra-search-page]') || document.querySelector('.aamra-search-page-heading-wrap') || document.querySelector('[data-aamra-search-empty]');
    if (!searchPageWrap) return;

    var terms = searchPageWrap.getAttribute('data-search-terms') || new URLSearchParams(window.location.search).get('q');
    var rootUrl = (searchPageWrap.getAttribute('data-root-url') || (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/$/, '') + '/';

    if (!terms) return;
    terms = terms.trim();

    var cleanLower = terms.toLowerCase();
    if (cleanLower === 'default' || cleanLower === 'default title' || cleanLower.indexOf('default') !== -1) {
      var headerBox = document.getElementById('aamra-correct-header-box');
      if (headerBox) headerBox.classList.add('hidden');
      var fuzzyHeaderBox = document.getElementById('aamra-fuzzy-header-box');
      if (fuzzyHeaderBox) fuzzyHeaderBox.classList.add('hidden');
      var zeroHeader = document.getElementById('aamra-zero-header-container');
      if (zeroHeader) zeroHeader.classList.remove('hidden');
      var noRes = document.getElementById('aamra-zero-no-results');
      if (noRes) noRes.textContent = 'No results for \u2018' + terms + '\u2019';
      var dym = document.getElementById('aamra-fuzzy-did-you-mean-zero');
      if (dym) dym.classList.add('hidden');
      var filterHeader = document.getElementById('header-filter');
      if (filterHeader) filterHeader.classList.add('hidden');
      var emptyContainer = document.getElementById('aamra-search-empty-container');
      if (emptyContainer) emptyContainer.classList.remove('hidden');
      var productGrid = document.getElementById('ProductGridContainer');
      if (productGrid) {
        var grid = productGrid.querySelector('#items-grid');
        if (grid) grid.style.display = 'none';
        var pag = productGrid.querySelector('.pagination');
        if (pag) pag.style.display = 'none';
      }
      return;
    }

    var urlParams = new URLSearchParams(window.location.search);
    var urlOriginalQ = urlParams.get('original_q') || urlParams.get('original_query');

    var countAttr = searchPageWrap.getAttribute('data-result-count');
    var count = countAttr !== null && countAttr !== '' ? parseInt(countAttr, 10) : null;
    var productCountElem = document.getElementById('ProductCount');
    if (productCountElem && count === null) {
      var tc = productCountElem.getAttribute('data-total-count');
      if (tc !== null && tc !== '') count = parseInt(tc, 10);
    }
    var realCountElem = document.getElementById('aamra-real-rendered-count');
    if (realCountElem && realCountElem.getAttribute('data-count') !== null) {
      var rc = parseInt(realCountElem.getAttribute('data-count'), 10);
      if (!isNaN(rc)) count = rc;
    } else {
      var grid = document.getElementById('items-grid');
      if (grid) {
        var visibleCards = grid.querySelectorAll('.grid-item:not(.hidden):not(.is-mobile-hidden)');
        if (visibleCards.length > 0) {
          count = visibleCards.length;
        }
      }
    }

    if (count !== null && !isNaN(count)) {
      var shopName = 'Aamra';
      var titleParts = document.title.split('–').map(function(s) { return s.trim(); });
      if (titleParts.length > 1) {
        shopName = titleParts[titleParts.length - 1];
      }
      document.title = 'Search: ' + count + ' ' + (count === 1 ? 'result' : 'results') + ' found for "' + terms + '" \u2013 ' + shopName;
      var tabAttElem = document.querySelector('[x-data*="xTabAttention"]');
      if (tabAttElem && tabAttElem._x_dataStack && tabAttElem._x_dataStack[0]) {
        tabAttElem._x_dataStack[0].pageTitle = document.title;
      }
    }

    var fuzzyHeader = document.getElementById('aamra-fuzzy-header-box');
    var correctHeader = document.getElementById('aamra-correct-header-box');

    // 1. If original_q is in the URL, this is Frame 2: "0 results from a search query - fuzzy match"
    if (urlOriginalQ && urlOriginalQ.toLowerCase() !== terms.toLowerCase()) {
      if (fuzzyHeader) {
        fuzzyHeader.classList.remove('hidden');
        var noResElem = document.getElementById('aamra-fuzzy-no-results');
        if (noResElem) {
          noResElem.textContent = 'No results for \u2018' + urlOriginalQ + '\u2019';
        }
        var dymLink = document.getElementById('aamra-fuzzy-link');
        if (dymLink) {
          dymLink.textContent = '\u2018' + terms + '\u2019';
          dymLink.href = rootUrl + 'search?q=' + encodeURIComponent(terms);
        }
      }
      if (correctHeader) {
        correctHeader.classList.remove('hidden');
        correctHeader.classList.add('mt-6', 'md:mt-8');
        var correctLabel = document.getElementById('aamra-correct-label');
        if (correctLabel) correctLabel.classList.add('hidden');
        var headingTitle = document.getElementById('aamra-search-heading-title');
        if (headingTitle) {
          headingTitle.innerHTML = 'Showing results for \u2018<span id="aamra-search-heading-term">' + aamraEscapeHtml(terms) + '</span>\u2019';
        }
      }

      // Log correction event
      var correctionEventData = {
        originalQuery: urlOriginalQ,
        correctedQuery: terms,
        timestamp: new Date().toISOString()
      };
      console.log('Search correction event:', correctionEventData);
      window.dispatchEvent(new CustomEvent('search:correction', { detail: correctionEventData }));
      try {
        var history = JSON.parse(localStorage.getItem('aamra_search_corrections') || '[]');
        if (!history.some(function(h) { return h.originalQuery === urlOriginalQ && h.correctedQuery === terms; })) {
          history.unshift(correctionEventData);
          localStorage.setItem('aamra_search_corrections', JSON.stringify(history.slice(0, 50)));
        }
      } catch (e) {}
      return;
    }

    // 2. If it is a non-empty results search and NO original_q, it is a correct spelling search!
    if (count !== null && count > 0) {
      if (fuzzyHeader) fuzzyHeader.classList.add('hidden');
      if (correctHeader) {
        correctHeader.classList.remove('hidden');
        correctHeader.classList.remove('mt-6', 'md:mt-8');
        var correctLabel = document.getElementById('aamra-correct-label');
        if (correctLabel) correctLabel.classList.remove('hidden');
        var headingTitle = document.getElementById('aamra-search-heading-title');
        if (headingTitle) {
          headingTitle.innerHTML = 'Showing results...';
        }
      }
      return;
    }

    // If active filters are applied, do not attempt fuzzy match redirects or zero-query headers
    if (window.location.search.indexOf('filter.') !== -1) {
      if (fuzzyHeader) fuzzyHeader.classList.add('hidden');
      if (correctHeader) correctHeader.classList.add('hidden');
      var zeroH = document.getElementById('aamra-zero-header-container');
      if (zeroH) zeroH.classList.add('hidden');
      return;
    }

    // 3. Zero results state (count === 0 and no original_q): check if search terms can be fuzzy matched!
    var dictionary = [
      'necklaces', 'necklace', 'letter necklace', 'initial necklace', 'gold necklace', 'chain necklace', 'pendant necklace', 'choker necklace',
      'rings', 'ring', 'signet ring', 'gold ring', 'sparkle ring', 'band ring', 'stacking ring',
      'earrings', 'earring', 'hoop earrings', 'stud earrings', 'drop earrings',
      'bracelets', 'bracelet', 'chain bracelet', 'cuff bracelet',
      'pendants', 'pendant', 'chains', 'chain', 'choker', 'chokers', 'anklets', 'anklet', 'bangles', 'bangle',
      'gold', 'silver', 'diamond', 'diamonds', 'emerald', 'gemstone', 'gemstones', 'pearl', 'pearls', 'locket'
    ];

    var dictScript = document.getElementById('aamra-search-dictionary');
    if (dictScript) {
      try {
        var parsed = JSON.parse(dictScript.textContent);
        if (Array.isArray(parsed) && parsed.length > 0) {
          dictionary = dictionary.concat(parsed);
        }
      } catch (e) {}
    }

    var fuzzySuggestion = findFuzzyMatch(terms, dictionary);
    if (fuzzySuggestion && fuzzySuggestion.toLowerCase() !== terms.toLowerCase()) {
      window.location.replace(rootUrl + 'search?q=' + encodeURIComponent(fuzzySuggestion) + '&original_q=' + encodeURIComponent(terms));
    }
  }

  // 4. Global Event Delegation (Clicks, Submissions, Focus)
  document.addEventListener('click', function(e) {
    var pill = e.target.closest('[data-aamra-search-pill]');
    if (pill) {
      e.preventDefault();
      var term = pill.getAttribute('data-aamra-search-pill') || pill.textContent.trim();
      if (term && term !== 'undefined' && window.aamraSearch) window.aamraSearch.applyPill(term);
      return;
    }

    var queryItem = e.target.closest('[data-aamra-query]');
    if (queryItem) {
      var queryText = queryItem.getAttribute('data-aamra-query') || queryItem.textContent.trim();
      if (queryText && window.aamraSearch) {
        window.aamraSearch.addRecent(queryText);
      }
      return;
    }
  });

  document.addEventListener('submit', function(e) {
    var form = e.target.closest('form[action*="/search"], [data-aamra-search-form]');
    if (form) {
      // Ensure search parameters always restrict to title and description (body)
      var fieldsInput = form.querySelector('input[name="options[fields]"]');
      if (fieldsInput) {
        fieldsInput.value = 'title,body';
      } else {
        fieldsInput = document.createElement('input');
        fieldsInput.type = 'hidden';
        fieldsInput.name = 'options[fields]';
        fieldsInput.value = 'title,body';
        form.appendChild(fieldsInput);
      }
      var typeInput = form.querySelector('input[name="type"]');
      if (!typeInput) {
        typeInput = document.createElement('input');
        typeInput.type = 'hidden';
        typeInput.name = 'type';
        typeInput.value = 'product';
        form.appendChild(typeInput);
      }
      if (!form.querySelector('input[name="options[prefix]"]')) {
        var prefixInput = document.createElement('input');
        prefixInput.type = 'hidden';
        prefixInput.name = 'options[prefix]';
        prefixInput.value = 'none';
        form.appendChild(prefixInput);
      }

      var qInput = form.querySelector('input[name="q"]');
      if (qInput) {
        var rawQuery = (qInput.value || '').trim();
        if (!rawQuery) {
          e.preventDefault();
          qInput.focus();
          return;
        }
        if (window.aamraSearch) window.aamraSearch.addRecent(rawQuery);

        var dictionary = [
          'necklaces', 'necklace', 'letter necklace', 'initial necklace', 'gold necklace', 'chain necklace', 'pendant necklace', 'choker necklace',
          'rings', 'ring', 'signet ring', 'gold ring', 'sparkle ring', 'band ring', 'stacking ring',
          'earrings', 'earring', 'hoop earrings', 'stud earrings', 'drop earrings',
          'bracelets', 'bracelet', 'chain bracelet', 'cuff bracelet',
          'pendants', 'pendant', 'chains', 'chain', 'choker', 'chokers', 'anklets', 'anklet', 'bangles', 'bangle',
          'gold', 'silver', 'diamond', 'diamonds', 'emerald', 'gemstone', 'gemstones', 'pearl', 'pearls', 'locket'
        ];
        var dictScript = document.getElementById('aamra-search-dictionary');
        if (dictScript) {
          try {
            var parsed = JSON.parse(dictScript.textContent);
            if (Array.isArray(parsed) && parsed.length > 0) {
              dictionary = dictionary.concat(parsed);
            }
          } catch (err) {}
        }

        var match = findFuzzyMatch(rawQuery, dictionary);
        if (match && match.toLowerCase() !== rawQuery.toLowerCase()) {
          e.preventDefault();
          var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
          rootUrl = rootUrl.replace(/\/$/, '') + '/';

          var correctionEventData = {
            originalQuery: rawQuery,
            correctedQuery: match,
            timestamp: new Date().toISOString()
          };
          console.log('Search correction event:', correctionEventData);
          window.dispatchEvent(new CustomEvent('search:correction', { detail: correctionEventData }));
          try {
            var history = JSON.parse(localStorage.getItem('aamra_search_corrections') || '[]');
            history.unshift(correctionEventData);
            localStorage.setItem('aamra_search_corrections', JSON.stringify(history.slice(0, 50)));
          } catch (err) {}

          window.location.href = rootUrl + 'search?q=' + encodeURIComponent(match) + '&original_q=' + encodeURIComponent(rawQuery) + '&type=product&options%5Bfields%5D=title,body&options%5Bprefix%5D=none';
          return;
        }
      }
    }
  });

  document.addEventListener('focusin', function(e) {
    if (e.target && (e.target.id === 'header-search-in-modal' || e.target.name === 'q')) {
      if (window.aamraSearch) window.aamraSearch.renderRecent();
    }
  });

  window.addEventListener('open-search-modal', function() {
    if (window.aamraSearch) {
      window.aamraSearch.renderRecent();
      window.aamraSearch.loadPopularFromApp();
    }
  });

  // 5. Page Load Execution
  try {
    if (window.location.pathname.indexOf('/search') !== -1) {
      var params = new URLSearchParams(window.location.search);
      var q = params.get('q');
      if (q && q.trim()) {
        window.aamraSearch.addRecent(q.trim());
        var needsReplace = false;
        if (!params.has('options[fields]') && !params.has('options%5Bfields%5D')) {
          params.set('options[fields]', 'title,body');
          needsReplace = true;
        }
        if (!params.has('options[prefix]') && !params.has('options%5Bprefix%5D')) {
          params.set('options[prefix]', 'none');
          needsReplace = true;
        }
        if (!params.has('type')) {
          params.set('type', 'product');
          needsReplace = true;
        }
        if (needsReplace) {
          window.location.replace(window.location.pathname + '?' + params.toString());
        }
      }
    }
  } catch (e) {}

  function syncSearchCount() {
    if (window.location.pathname.indexOf('/search') === -1) return;
    var grid = document.getElementById('items-grid');
    if (!grid) return;
    var cards = grid.querySelectorAll('.grid-item:not(.hidden):not(.is-mobile-hidden)');
    var cardCount = cards.length;
    if (cardCount === 0) return;

    var showingDesktop = document.querySelector('.showing-items-count .showing-count-desktop');
    var showingMobile = document.querySelector('.showing-items-count .showing-count-mobile');
    var showingP = document.querySelector('.showing-items-count');

    if (showingDesktop) {
      showingDesktop.textContent = cardCount + ' of ' + cardCount + ' products';
    }
    if (showingMobile) {
      showingMobile.textContent = cardCount + ' of ' + cardCount + ' products';
    }
    if (!showingP) {
      var gridParent = grid.parentElement;
      if (gridParent) {
        var wrap = document.createElement('div');
        wrap.className = 'flex justify-center mt-3 md:mt-9 mb-7 border-t pt-7';
        wrap.innerHTML = '<div class="w-full flex flex-col items-center justify-center"><p class="mb-3 showing-items-count"><span class="showing-count-mobile">' + cardCount + ' of ' + cardCount + ' products</span><span class="showing-count-desktop">' + cardCount + ' of ' + cardCount + ' products</span></p></div>';
        gridParent.appendChild(wrap);
      }
    }
    var btnPagination = document.querySelector('[id*="btn-pagination"]');
    if (cardCount <= 50 && btnPagination) {
      var loadMoreBtn = btnPagination.querySelector('.load-more-btn');
      if (loadMoreBtn) loadMoreBtn.style.display = 'none';
    }

    // Keep document.title completely synced with actual rendered count
    var searchPageWrap = document.querySelector('[data-aamra-search-page]') || document.querySelector('.aamra-search-page-heading-wrap');
    var terms = searchPageWrap ? searchPageWrap.getAttribute('data-search-terms') : new URLSearchParams(window.location.search).get('q');
    if (terms && terms.trim()) {
      var shopName = 'Aamra';
      var titleParts = document.title.split('–').map(function(s) { return s.trim(); });
      if (titleParts.length > 1) {
        shopName = titleParts[titleParts.length - 1];
      }
      document.title = 'Search: ' + cardCount + ' ' + (cardCount === 1 ? 'result' : 'results') + ' found for "' + terms.trim() + '" \u2013 ' + shopName;
      var tabAttElem = document.querySelector('[x-data*="xTabAttention"]');
      if (tabAttElem && tabAttElem._x_dataStack && tabAttElem._x_dataStack[0]) {
        tabAttElem._x_dataStack[0].pageTitle = document.title;
      }
    }
  }

  function initAamraSearch() {
    if (window.aamraSearch) {
      window.aamraSearch.renderRecent();
      window.aamraSearch.loadPopularFromApp();
    }
    initFuzzySearch();
    syncSearchCount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAamraSearch);
  } else {
    initAamraSearch();
  }
})();

